import type * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { Collapsible } from '../../../components/Collapsible';
import { AdvancedOptionsButton } from '../../../components/automation/AutomationStepCard/presentation';
import { styleSheet } from '../../../components/automation/AutomationStepCard/styles';
import { ConditionalFilter } from './ConditionalFilter';

/**
 * StepAdvancedOptionsPanel component.
 *
 * A shared wrapper for advanced options sections in automation step renderers.
 * Provides consistent structure for:
 * - Collapsible toggle button
 * - Collapsible content area
 * - Conditional contact filter
 *
 * Step-specific content (like StepSendFromOption) can be passed as children.
 *
 * @example
 * ```tsx
 * <StepAdvancedOptionsPanel
 *   isExpanded={advancedOptionsToggled}
 *   onToggle={() => setAdvancedOptionsToggled(!advancedOptionsToggled)}
 *   stepId={stepId}
 *   filter={skipStepContactFilter}
 *   canEdit={canEdit}
 *   isBusy={step?.isBusy}
 *   onFilterChange={handleFilterChange}
 * >
 *   <StepSendFromOption ... />
 * </StepAdvancedOptionsPanel>
 * ```
 */
interface StepAdvancedOptionsPanelProps {
	/** Whether the advanced options are expanded (visible) */
	isExpanded: boolean;
	/** Handler for toggling expanded state */
	onToggle: () => void;
	/** The step identifier for the filter */
	stepId: string;
	/** Current contact filter criteria */
	filter: Api.IContactsFilterRequest | null | undefined;
	/** Whether the step can be edited */
	canEdit: boolean;
	/** Whether the step is busy/loading */
	isBusy?: boolean;
	/** Handler for filter changes */
	onFilterChange: (filter: Api.IContactsFilterRequest | null) => void;
	/** Step-specific content to render above the ConditionalFilter */
	children?: React.ReactNode;
}

export function StepAdvancedOptionsPanel({
	isExpanded,
	onToggle,
	stepId,
	filter,
	canEdit,
	isBusy,
	onFilterChange,
	children,
}: StepAdvancedOptionsPanelProps) {
	return (
		<>
			<AdvancedOptionsButton isCollapsed={!isExpanded} onClick={onToggle} />
			<Collapsible isCollapsed={!isExpanded}>
				<div className={css(styleSheet.advancedOptionsContent)}>
					{children}
					<ConditionalFilter
						stepId={stepId}
						filter={filter}
						canEdit={canEdit}
						isBusy={isBusy}
						onFilterChange={onFilterChange}
					/>
				</div>
			</Collapsible>
		</>
	);
}
